import React, { useEffect, useState } from "react";
import { FaHandshake, FaProjectDiagram, FaRedo, FaClock } from "react-icons/fa";
import { FaPaperPlane, FaPhoneAlt, FaFileAlt } from "react-icons/fa";
import {
  CheckCircle,
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  Star,
} from "lucide-react";

export default function PartnerWithUsWallTexture() {
  const [ContactPageData, setContactPageData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // Holds success/error response messages

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await fetch(
          "https://webadmin.rigve.com/api/get_extra_details",
        );
        const result = await response.json();

        if (result.status === 1) {
          setContactPageData(result.data);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchData();
  }, []);

  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    category: "",
    message: "",
  });

  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!form.name) e.name = "Required";
    if (!form.company) e.company = "Required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Valid email required";
    if (!/^\d{10}$/.test(form.phone)) e.phone = "10 digit phone";
    if (!form.category) e.category = "Select category";
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setSubmitStatus(null);
    const e = validate();
    setErrors(e);

    if (!Object.keys(e).length) {
      setLoading(true);

      // Map local form fields to match the exact API payload payload format:
      const payload = {
        name: form.name,
        phone: Number(form.phone), // Converted to number as shown in your payload format
        email: form.email,
        company_name: form.company, // Remapped key
        category: form.category.toLowerCase(), // Matched format (e.g., "designer")
        msg: form.message, // Remapped key
      };

      try {
        const response = await fetch(
          "https://webadmin.rigve.com/api/save_partner",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          },
        );

        const result = await response.json();

        if (response.ok) {
          // Success feedback state update
          setSubmitStatus({
            type: "success",
            message: result.message || "Partner details stored successfully!",
          });

          // Reset form fields
          setForm({
            name: "",
            company: "",
            email: "",
            phone: "",
            category: "",
            message: "",
          });
          setErrors({});
        } else {
          setSubmitStatus({
            type: "error",
            message:
              result.message || "Failed to store data. Please try again.",
          });
        }
      } catch (error) {
        console.error("API Error:", error);
        setSubmitStatus({
          type: "error",
          message: "An error occurred while submitting data to the server.",
        });
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="bg-white text-slate-800">
      <section id="form" className="w-full bg-orange-100">
        <div className="py-8 max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* LEFT - IMAGE + CONTENT */}
            <div>
              <div className="relative">
                <img
                  src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?q=80&w=1200&auto=format&fit=crop"
                  alt="Wall Texture Design"
                  className="rounded-2xl shadow-xl object-cover h-[450px] w-full"
                />
                <div className="absolute bottom-6 left-6 right-6 bg-white/90 backdrop-blur-lg p-6 rounded-2xl shadow-lg">
                  <h3 className="text-md font-semibold text-gray-900">
                    Premium Wall Texture Solutions
                  </h3>
                  <p className="text-gray-600 text-sm mt-2">
                    From modern finishes to luxury designs, we provide top-tier
                    wall texture products trusted by designers and contractors
                    across India.
                  </p>
                </div>
              </div>
              <h2 className="fs-21 mt-6 font-bold text-gray-900 leading-tight">
                Partner With Us &{" "}
                <span className="text-orange-500">Grow Your Business</span>
              </h2>
              <p className="mt-4 text-gray-600 max-w-lg">
                Join our network of professionals and unlock access to
                high-quality materials, expert support, and fast-growing
                opportunities in the wall texture industry.
              </p>
              <div className="mt-6 grid grid-cols-2 gap-4 text-sm">
                {[
                  "No Registration Fee",
                  "High Margin Products",
                  "Dedicated Support",
                  "Pan India Network",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2">
                    <CheckCircle className="text-orange-500" size={18} />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* RIGHT - FORM WITH INLINE ALERT */}
            <div>
              <form
                onSubmit={submit}
                className="relative bg-white/90 backdrop-blur-xl border border-orange-100 shadow-2xl rounded-2xl p-6 space-y-5"
              >
                <h3 className="fs-21 font-semibold text-gray-800 mb-2">
                  Express Your Interest
                </h3>

                {/* Inline Server Response Banner */}
                {submitStatus && (
                  <div
                    className={`p-4 rounded-xl text-sm font-medium ${
                      submitStatus.type === "success"
                        ? "bg-green-100 border border-green-300 text-green-800"
                        : "bg-red-100 border border-red-300 text-red-800"
                    }`}
                  >
                    {submitStatus.message}
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <input
                      className="w-full text-sm px-4 py-3 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition"
                      placeholder="Full Name"
                      value={form.name}
                      onChange={(e) =>
                        setForm({ ...form, name: e.target.value })
                      }
                    />
                    {errors.name && (
                      <p className="text-red-500 mt-1 text-xs">{errors.name}</p>
                    )}
                  </div>
                  <div>
                    <input
                      className="w-full text-sm px-4 py-3 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition"
                      placeholder="Company Name"
                      value={form.company}
                      onChange={(e) =>
                        setForm({ ...form, company: e.target.value })
                      }
                    />
                    {errors.company && (
                      <p className="text-red-500 mt-1 text-xs">
                        {errors.company}
                      </p>
                    )}
                  </div>
                </div>
                <div>
                  <input
                    className="w-full text-sm px-4 py-3 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition"
                    placeholder="Email Address"
                    value={form.email}
                    onChange={(e) =>
                      setForm({ ...form, email: e.target.value })
                    }
                  />
                  {errors.email && (
                    <p className="text-red-500 mt-1 text-xs">{errors.email}</p>
                  )}
                </div>
                <div>
                  <input
                    className="w-full text-sm px-4 py-3 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition"
                    placeholder="Phone Number"
                    value={form.phone}
                    onChange={(e) =>
                      setForm({ ...form, phone: e.target.value })
                    }
                  />
                  {errors.phone && (
                    <p className="text-red-500 mt-1 text-xs">{errors.phone}</p>
                  )}
                </div>
                <div>
                  <select
                    className="w-full text-sm px-4 py-3 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition"
                    value={form.category}
                    onChange={(e) =>
                      setForm({ ...form, category: e.target.value })
                    }
                  >
                    <option value="">Select Category</option>
                    <option value="Dealer">Dealer</option>
                    <option value="Designer">Designer</option>
                    <option value="Contractor">Contractor</option>
                  </select>
                  {errors.category && (
                    <p className="text-red-500 mt-1 text-xs">
                      {errors.category}
                    </p>
                  )}
                </div>
                <textarea
                  className="w-full text-sm px-4 py-3 rounded-xl border border-gray-200 focus:border-orange-400 focus:ring-2 focus:ring-orange-100 outline-none transition min-h-[120px]"
                  placeholder="Tell us about your business..."
                  value={form.message}
                  onChange={(e) =>
                    setForm({ ...form, message: e.target.value })
                  }
                />
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3 rounded-xl bg-gradient-to-r from-orange-500 to-orange-600 text-white font-semibold flex items-center justify-center gap-2 hover:scale-[1.02] transition disabled:opacity-50"
                >
                  {loading ? "Submitting..." : "Submit Request"}{" "}
                  <ArrowRight size={18} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER CONTACT INFORMATION SECTION */}
      <section className="bg-white mx-auto px-4 max-w-7xl py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative w-full h-full bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-2xl p-10 shadow-xl overflow-hidden">
            <div className="absolute inset-0 opacity-10 bg-[url('https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1200&q=80')]"></div>
            <div className="relative flex flex-col align-middle justify-center h-full z-10">
              <h2 className="fs-21 font-bold">
                Let’s Build Something Amazing Together
              </h2>
              <p className="mt-4 text-orange-100">
                Join our wall texture network and grow your business with
                premium finishes, expert support, and trusted quality.
              </p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="flex flex-row items-center gap-5 p-6 rounded-2xl border border-gray-100 shadow-md hover:shadow-xl transition hover:-translate-y-1 bg-white">
              <div className="bg-orange-100 p-4 rounded-xl text-orange-500">
                <Mail size={26} />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-left">Email Us</h3>
                <a
                  href={`mailto:${ContactPageData?.email}`}
                  className="text-gray-600 text-left"
                >
                  {ContactPageData?.email}
                </a>
              </div>
            </div>

            <div className="flex flex-row items-center gap-5 p-6 rounded-2xl border border-gray-100 shadow-md hover:shadow-xl transition hover:-translate-y-1 bg-white">
              <div className="bg-orange-100 p-4 rounded-xl text-orange-500">
                <Phone size={26} />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-left">Call Us</h3>
                <a
                  href={`tel:${ContactPageData?.phone}`}
                  className="text-gray-600 text-left"
                >
                  {ContactPageData?.phone}
                </a>
              </div>
            </div>

            <div className="flex flex-row items-center gap-5 p-6 rounded-2xl border border-gray-100 shadow-md hover:shadow-xl transition hover:-translate-y-1 bg-white">
              <div className="bg-orange-100 p-4 rounded-xl text-orange-500">
                <MapPin size={26} />
              </div>
              <div>
                <h3 className="font-semibold text-lg text-left">Head Office</h3>
                <a
                  href={ContactPageData?.address_1_link}
                  target="_blank"
                  rel="noreferrer"
                  className="text-gray-600 text-left"
                >
                  {ContactPageData?.address1}
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
